import { useMemo, type ReactNode } from 'react';
import { json } from '@codemirror/lang-json';
import { okaidiaInit } from '@uiw/codemirror-theme-okaidia';
import CodeMirror from '@uiw/react-codemirror';

import { LocalTime } from '~/components/common/LocalTime';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import {
  Button,
  Modal,
  ModalActions,
  PanelTable,
  SkeletonFields,
  type PanelTableColumn,
  StatusChip,
  LoadError,
} from '~/components/ui';
import { apiErrorMessage } from '~/lib/apiError';
import { themeColor } from '~/lib/theme-colors';
import { useGetDigitalAssistantVersionQuery } from '~/modules/query/ai/digitalAssistants';

export interface AssistantVersionPreviewModalProps {
  assistantId: string;
  /** The version to show, or null when the modal is closed (it lives in `?version=`). */
  versionNumber: number | null;
  onClose: () => void;
  onRestore: (versionNumber: number) => void;
}

interface DetailRow {
  label: string;
  value: ReactNode;
}

/**
 * A saved version exactly as it was stored — not the current draft. Restore
 * hands back to the page, which confirms before replacing the form.
 */
export default function AssistantVersionPreviewModal({
  assistantId,
  versionNumber,
  onClose,
  onRestore,
}: AssistantVersionPreviewModalProps) {
  const { data, error, isFetching, refetch } = useGetDigitalAssistantVersionQuery(
    { id: assistantId, versionNumber: versionNumber ?? 0 },
    { skip: versionNumber === null },
  );
  const version = data?.data && data.data.versionNumber === versionNumber ? data.data : undefined;
  const extensions = useMemo(() => [json()], []);
  const theme = useMemo(
    () => okaidiaInit({ settings: { caret: themeColor('gray-300'), fontFamily: 'monospace' } }),
    [],
  );

  const rows: DetailRow[] = version
    ? [
        { label: 'Version', value: `#${version.versionNumber}` },
        { label: 'Name', value: version.name },
        {
          label: 'Description',
          value: version.description || <span className="text-gray-400 dark:text-gray-500">—</span>,
        },
        {
          label: 'Agent',
          value: version.agentNameSnapshot || (
            <span className="text-gray-400 dark:text-gray-500">—</span>
          ),
        },
        { label: 'AI model', value: version.model },
        {
          label: 'Assistant key',
          value: (
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
              {version.assistantKey}
            </span>
          ),
        },
        { label: 'Status', value: <StatusChip {...aiStatus(version.status)} /> },
        {
          label: 'Created by',
          value:
            version.createdByUser?.displayName || version.createdByUser?.email || version.createdBy,
        },
        { label: 'Created', value: <LocalTime value={version.createdAt} /> },
      ]
    : [];

  const columns: PanelTableColumn<DetailRow>[] = [
    {
      header: 'Field',
      width: 'w-44',
      render: (row) => <span className="text-gray-500 dark:text-gray-400">{row.label}</span>,
    },
    { header: 'Value', render: (row) => row.value },
  ];

  return (
    <Modal
      isOpen={versionNumber !== null}
      onClose={onClose}
      title={version ? `Version ${version.versionNumber} of ${version.name}` : 'Version preview'}
      testId="ai-digital-assistants-version-preview"
      footer={
        <ModalActions
          onDismiss={onClose}
          dismissLabel="Cancel"
          dismissTestId="ai-digital-assistants-version-preview-close"
        >
          <Button
            disabled={!version || isFetching}
            onClick={() => versionNumber !== null && onRestore(versionNumber)}
            testId="ai-digital-assistants-version-preview-restore"
          >
            Restore this version
          </Button>
        </ModalActions>
      }
    >
      {isFetching && !version ? (
        <SkeletonFields fields={6} />
      ) : error ? (
        <LoadError
          what="this version"
          message={apiErrorMessage(error)}
          onRetry={() => refetch()}
          testId="ai-digital-assistants-version-preview"
        />
      ) : version ? (
        <div className="space-y-6">
          <PanelTable<DetailRow>
            ariaLabel="Version details"
            columns={columns}
            data={rows}
            getRowId={(row) => row.label}
            emptyMessage="This version has no details."
            emptyDescription="Pick another version from the history."
          />

          <section className="space-y-2">
            <p className="text-sm font-medium text-gray-800 dark:text-white/90">Input schema</p>
            {version.inputPayloadSchema ? (
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800">
                <CodeMirror
                  aria-label="Input schema of this version"
                  value={JSON.stringify(version.inputPayloadSchema, null, 2)}
                  height="240px"
                  theme={theme}
                  extensions={extensions}
                  editable={false}
                  basicSetup={{ foldGutter: true, highlightActiveLine: false, lineNumbers: true }}
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This version has no input schema.
              </p>
            )}
          </section>

          <section className="space-y-2">
            <p className="text-sm font-medium text-gray-800 dark:text-white/90">Prompt</p>
            <pre className="max-h-[40vh] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
              {version.promptTemplate}
            </pre>
          </section>
        </div>
      ) : null}
    </Modal>
  );
}
